import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ChefHat, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  CheckCircle2,
  Clock,
  FileText,
  Settings,
  Package
} from 'lucide-react';

const RecordCard = ({ record, onClick, formatDate }) => {
  const kitchenStatus = record.kitchen_status;
  const handover = record.kitchen_handover;
  
  return (
    <Card 
      className={`cursor-pointer hover:shadow-lg transition-all ${
        kitchenStatus === 'confirmed' ? 'border-green-200 bg-green-50/30' : 
        kitchenStatus === 'draft' ? 'border-yellow-200 bg-yellow-50/30' : 
        'hover:border-orange-300'
      }`}
      onClick={onClick}
      data-testid={`kitchen-record-${record.id}`}
    >
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div>
            <p className="font-mono text-sm text-slate-500">{record.doklad}</p>
            <h3 className="font-semibold text-slate-800 mt-1 line-clamp-2">
              {record.data?.Popis || record.data?.Výrobek || 'Bez názvu'}
            </h3>
          </div>
          {kitchenStatus === 'confirmed' ? (
            <Badge className="bg-green-100 text-green-700 border-green-200">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Předáno
            </Badge>
          ) : kitchenStatus === 'draft' ? (
            <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
              <FileText className="w-3 h-3 mr-1" />
              Rozpracováno
            </Badge>
          ) : (
            <Badge variant="outline" className="text-orange-600 border-orange-300">
              <Clock className="w-3 h-3 mr-1" />
              Čeká
            </Badge>
          )}
        </div>
        
        <div className="space-y-1 text-sm text-slate-600">
          {record.data?.Šarže && (
            <div className="flex justify-between">
              <span className="text-slate-500">Šarže:</span>
              <span className="font-medium">{record.data.Šarže}</span>
            </div>
          )}
          {record.data?.['Množ.plán.'] && (
            <div className="flex justify-between">
              <span className="text-slate-500">Plán. množství:</span>
              <span className="font-medium text-blue-600">{record.data['Množ.plán.']}</span>
            </div>
          )}
          {record.data?.['Skut. množství'] && (
            <div className="flex justify-between">
              <span className="text-slate-500">Skut. množství:</span>
              <span className="font-medium">{record.data['Skut. množství']}</span>
            </div>
          )}
        </div>
        
        {handover && handover.items?.length > 0 && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-xs text-slate-500 mb-1">Předávka:</p>
            <div className="flex flex-wrap gap-1">
              {handover.items.slice(0, 3).map((item, idx) => (
                <span key={idx} className="text-xs bg-slate-100 px-2 py-0.5 rounded">
                  {item.name}: {item.quantity} {item.unit}
                </span>
              ))}
            </div>
          </div>
        )}
        
        <div className="mt-3 pt-3 border-t flex justify-between items-center">
          <span className="text-xs text-slate-400">
            {record.data?.['Plán. datum od'] 
              ? formatDate(record.data['Plán. datum od'])
              : formatDate(record.updated_at)}
          </span>
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </div>
      </CardContent>
    </Card>
  );
};

export const KitchenPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchRecords();
  }, [page, statusFilter]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      fetchRecords();
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), limit: '50' });
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await api.get(`/kitchen/production-records?${params}`);
      setRecords(response.data.records || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching records:', error);
      toast.error('Chyba při načítání záznamů');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('cs-CZ');
    } catch (e) {
      return dateStr;
    }
  };

  const pendingCount = records.filter(r => r.kitchen_status === 'not_started' || r.kitchen_status === 'draft').length;
  const confirmedCount = records.filter(r => r.kitchen_status === 'confirmed').length;

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <ChefHat className="w-7 h-7 text-orange-600" />
              Kuchyně
            </h1>
            <p className="text-slate-500 mt-1">Předávka výroby pro balírnu</p>
          </div>
          
          <Button 
            variant="outline"
            onClick={() => navigate('/admin/kuchyne')}
            data-testid="kitchen-settings-button"
          >
            <Settings className="w-4 h-4 mr-2" />
            Nastavení
          </Button>
        </div>

        {/* Quick filters */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={statusFilter === '' ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setStatusFilter(''); setPage(1); }}
            className={statusFilter === '' ? 'bg-slate-800' : ''}
          >
            Vše
          </Button>
          <Button
            variant={statusFilter === 'pending' ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setStatusFilter('pending'); setPage(1); }}
            className={statusFilter === 'pending' ? 'bg-orange-500 hover:bg-orange-600' : ''}
          >
            <Clock className="w-4 h-4 mr-1" />
            Čeká ({pendingCount})
          </Button>
          <Button
            variant={statusFilter === 'confirmed' ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setStatusFilter('confirmed'); setPage(1); }}
            className={statusFilter === 'confirmed' ? 'bg-green-500 hover:bg-green-600' : ''}
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Předáno ({confirmedCount})
          </Button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Hledat podle dokladu, názvu nebo šarže..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-kitchen-input"
            />
          </div>
        </div>

        {/* Records grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : records.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
            <ChefHat className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">
              {searchTerm ? 'Žádné záznamy nenalezeny' : 'Žádné záznamy k předání'}
            </h3>
            <p className="text-slate-400">
              Zkontrolujte filtry v nastavení
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {records.map((record) => (
                <RecordCard
                  key={record.id}
                  record={record}
                  onClick={() => navigate(`/kuchyne/${record.id}`)}
                  formatDate={formatDate}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Předchozí
                </Button>
                
                <span className="px-4 text-sm text-slate-600">
                  Strana {page} z {totalPages}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Další
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default KitchenPage;
